import React from "react";
import RenderIfVisible from "react-render-if-visible";
import {
  OptimizedGantt,
  ProjectElementType,
  list,
  resource,
  WorkLoadsCalendar,
} from "@planzone/dto";

import { ProjectLoader } from "@scenes/wbs/projectLoader";
import { filter as filterData } from "@common/filter";
import { DragAndDrop, Draggable } from "@common/dragAndDrop";
import { QuickCreationMenuWrapper } from "@common/quickCreationMenuWrapper";
import { ElementCreationForm, ElementTypes } from "@common/elementCreationForm";
import { ActivityWatcher } from "@common/activityWatcher";
import { useUtils } from "@common/treeToggle";
import { ResourceCtx } from "./resourceCtx";
import { ProjectCtx } from "./projectCtx";
import { Context } from "./context";
import { renderWithTreeCtx } from "./utils";
import { ProjectElementsCounter } from "./projectElementsCounter";
import { Project as ProjectComponent } from "./project";
import { List } from "./list";
import { Task } from "./task";
import { Milestone } from "./milestone";
import { Event } from "./event";
import { Resource } from "./resource";
import { Preview } from "./preview";
import { DropBox } from "./dropBox";
import { Placeholder } from "./placeholder";
import { QuickElementLoader } from "./quickElementLoader";
import { Loader } from "./loader";
import { Props } from "./types";
import styles from "./styles.scss";

const DEFAULT_ESTIMATED_HEIGHT = 40;

function canAddAfter(
  gantt: OptimizedGantt,
  list: OptimizedGantt[number],
  breadcrumbs: OptimizedGantt,
  index: number,
): boolean {
  const currentElement = gantt[index];
  const nextElement = gantt[index + 1];
  const isLastElement = !nextElement;
  const isAtListEnd =
    !isLastElement &&
    nextElement.depth <= list.depth &&
    !!breadcrumbs[breadcrumbs.length - 1] &&
    breadcrumbs[breadcrumbs.length - 1].uuid === list.uuid;
  const isNextToTarget =
    !!nextElement &&
    currentElement.uuid === list.uuid &&
    currentElement.depth >= nextElement.depth;

  return (
    isNextToTarget ||
    (!isLastElement
      ? isAtListEnd ||
        (!!breadcrumbs.find(({ uuid }) => uuid === list.uuid) &&
          nextElement.depth <= list.depth)
      : !!breadcrumbs.find(({ uuid }) => uuid === list.uuid) ||
        list.uuid === gantt[index].uuid)
  );
}

function Wbs({
  workLoads,
  projects,
  filter,
  sort,
  toggledLists,
  meta = {},
  style = {},
  loaderId = "wbs",
  configuration,
  hoveredElement,
  hoverElement,
  leaveElement,
  toggleList,
  toggleResource,
  setProjectElementsNbr,
  isFetchingProjects,
  isFetchingWorkloads,
  zone,
}: Props) {
  const { isOpen } = useUtils();
  const [openedCreationMenuMap, setOpenedCreationMenuMap] = React.useState<{
    [key: string]: OptimizedGantt[number];
  }>({});
  const [isDragging, setIsDragging] = React.useState(false);
  const [addInElement, setAddInElement] = React.useState<
    null | OptimizedGantt[number]
  >(null);
  const [addAfterAtElement, setAddAfterElement] = React.useState<
    null | OptimizedGantt[number]
  >(null);
  const inRef = React.useRef<HTMLDivElement>(null);
  const afterRef = React.useRef<HTMLDivElement>(null);

  const indentSize =
    style && style.indentSize
      ? typeof style.indentSize === "number"
        ? `${style.indentSize}px`
        : style.indentSize
      : `3rem`;

  ActivityWatcher.useActivityWatcher(inRef, {
    // blur: () => setAddInElement(null),
  });

  ActivityWatcher.useActivityWatcher(afterRef, {
    // blur: () => setAddAfterElement(null),
  });

  const filteredWorkLoads = filterData(workLoads, filter, (workLoad) => ({
    resource: {
      ...resource,
      uuid: workLoad.uuid,
      name: workLoad.resourceName,
      tags: workLoad.resourceTags.map((name) => ({ name })),
    },
  }));

  React.useEffect(() => {
    if (setProjectElementsNbr) {
      filteredWorkLoads.forEach((workLoad) => {
        const isResourceOpen = toggledLists[workLoad.uuid];
        if (!isResourceOpen) {
          setProjectElementsNbr(workLoad.uuid, 1);
        }
      });
    }
  }, [filteredWorkLoads, toggledLists, setProjectElementsNbr]);

  const renderResourceElements = (workLoad: WorkLoadsCalendar[number]) => {
    return (
      <ResourceCtx resource={workLoad} sort={sort} zone={zone}>
        {(projectsForResource, isLoadingResource) => {
          if (isLoadingResource) {
            return (
              <div
                style={{
                  marginLeft:
                    typeof style.indentSize === "number"
                      ? `${style.indentSize}px`
                      : `3rem`,
                }}
              >
                <Loader style={style} />
              </div>
            );
          }

          const filteredProjects = filterData(
            projectsForResource,
            filter,
            (project) => ({ project }),
          );

          return (
            <>
              {filteredProjects.map((project) => (
                <ProjectCtx
                  key={project.uuid}
                  workLoad={workLoad}
                  project={project}
                  filter={filter}
                  sort={sort}
                  isMultiProject={filteredProjects.length > 1}
                  hideProjects={false}
                >
                  {(ganttByProject, isLoadingProject) => {
                    if (isLoadingProject)
                      return (
                        <div
                          style={{
                            marginLeft:
                              typeof style.indentSize === "number"
                                ? `${style.indentSize}px`
                                : `3rem`,
                          }}
                        >
                          <Loader style={style} />
                        </div>
                      );

                    const shouldShowPlaceholder = ganttByProject.length === 0;

                    return (
                      <>
                        <ProjectElementsCounter
                          ganttByProject={ganttByProject}
                          workLoad={workLoad}
                          projectUuid={project.uuid}
                          setProjectElementsNbr={setProjectElementsNbr}
                          toggledLists={toggledLists}
                        />

                        {shouldShowPlaceholder && (
                          <Placeholder project={project} filter={filter} />
                        )}

                        <DropBox
                          type="dropStart"
                          gantt={ganttByProject}
                          parentList={project.rootList}
                        />

                        {renderWithTreeCtx(
                          ganttByProject,
                          isOpen,
                          workLoad,
                          (element, ctx, index, _, breadcrumbs) => {
                            const isProject =
                              element.type === ProjectElementType.PROJECT;
                            const isList =
                              element.type === ProjectElementType.LIST;
                            const isTask =
                              element.type === ProjectElementType.TASK;
                            const isMilestone =
                              element.type === ProjectElementType.MILESTONE;
                            const isEvent =
                              element.type === ProjectElementType.EVENT;
                            const isListEnd = !!ganttByProject[index + 1]
                              ? ganttByProject[index + 1].depth < element.depth
                              : true;

                            const parentList =
                              element.parentUuid && element.depth > 0
                                ? {
                                    ...list,
                                    uuid: element.parentUuid,
                                  }
                                : {
                                    ...list,
                                    uuid:
                                      project?.rootList.uuid ||
                                      element.activityRootUuid,
                                  };

                            const elMeta = meta[element.uuid] || {
                              uuid: element.uuid,
                              allocatedHours: 0,
                              attachmentCount: 0,
                              commentCount: 0,
                              elementCount: 0,
                              duration: 0,
                              percentComplete: 0,
                              spentHours: 0,
                            };

                            return (
                              <div
                                key={element.uuid}
                                onMouseEnter={() =>
                                  hoverElement && hoverElement(element)
                                }
                                onMouseLeave={() =>
                                  leaveElement && leaveElement(element)
                                }
                              >
                                <div
                                  style={{
                                    marginLeft: `calc(${element.depth + 1} * ${indentSize})`,
                                    height:
                                      (style.itemHeight ||
                                        DEFAULT_ESTIMATED_HEIGHT) *
                                      (openedCreationMenuMap[element.uuid]
                                        ? 2
                                        : 1),
                                  }}
                                >
                                  <RenderIfVisible
                                    root={document.getElementById(loaderId)}
                                    defaultHeight={
                                      style.itemHeight ||
                                      DEFAULT_ESTIMATED_HEIGHT
                                    }
                                    placeholderElement="div"
                                    placeholderElementClass={styles.placeholder}
                                    stayRendered
                                  >
                                    <DropBox
                                      type={isList ? "dropInList" : "dropAtEl"}
                                      style={{
                                        height:
                                          style.itemHeight ||
                                          DEFAULT_ESTIMATED_HEIGHT,
                                      }}
                                      parentList={
                                        isList
                                          ? { ...list, uuid: element.uuid }
                                          : parentList
                                      }
                                      gantt={ganttByProject}
                                      projectElement={element}
                                      index={index}
                                    >
                                      {(_, canDrop) => (
                                        <QuickCreationMenuWrapper
                                          project={project}
                                          parentList={
                                            isList
                                              ? { ...list, uuid: element.uuid }
                                              : parentList
                                          }
                                          isInactive={isDragging}
                                          range={{
                                            min: isList
                                              ? null
                                              : {
                                                  ...element,
                                                  rank: element.referenceKeyRank,
                                                },
                                            max: !!ganttByProject[index + 1]
                                              ? {
                                                  ...ganttByProject[index + 1],
                                                  rank: ganttByProject[
                                                    index + 1
                                                  ].referenceKeyRank,
                                                }
                                              : null,
                                          }}
                                          loader={(type, name) => (
                                            <div
                                              className={
                                                styles.elementLoaderWrapper
                                              }
                                            >
                                              <QuickElementLoader
                                                type={type}
                                                name={name}
                                                parentList={parentList}
                                              />
                                            </div>
                                          )}
                                          isThick={isList}
                                          toggle={(isOpen) => {
                                            setOpenedCreationMenuMap((m) => {
                                              if (isOpen) {
                                                return {
                                                  ...m,
                                                  [element.uuid]: element,
                                                };
                                              }
                                              return Object.fromEntries(
                                                Object.entries(m).filter(
                                                  ([key]) =>
                                                    key !== element.uuid,
                                                ),
                                              );
                                            });
                                          }}
                                        >
                                          <Draggable
                                            className={styles.draggable}
                                            type={
                                              isList
                                                ? "MOVE_LIST"
                                                : isTask
                                                  ? "MOVE_TASK"
                                                  : isMilestone
                                                    ? "MOVE_MILESTONE"
                                                    : "MOVE_EVENT"
                                            }
                                            data={{
                                              projectElement: element,
                                              parentList,
                                            }}
                                            startDrag={() =>
                                              setIsDragging(true)
                                            }
                                            stopDrag={() =>
                                              setIsDragging(false)
                                            }
                                          >
                                            {isProject ? (
                                              <ProjectComponent
                                                project={element}
                                                workLoad={workLoad}
                                                meta={elMeta}
                                                filter={filter}
                                                isOpen={
                                                  toggledLists[
                                                    `${workLoad.uuid}:${element.uuid}`
                                                  ]
                                                }
                                                toggle={(isOpen) =>
                                                  toggleList(
                                                    element.uuid,
                                                    workLoad.uuid,
                                                    isOpen,
                                                  )
                                                }
                                                isMultiProject={
                                                  filteredProjects.length > 1
                                                }
                                              />
                                            ) : isList ? (
                                              <List
                                                list={element}
                                                meta={elMeta}
                                                parentList={parentList}
                                                workLoad={workLoad}
                                                isOpen={
                                                  toggledLists[
                                                    `${workLoad.uuid}:${element.uuid}`
                                                  ]
                                                }
                                                toggle={(isOpen) =>
                                                  toggleList(
                                                    element.uuid,
                                                    workLoad.uuid,
                                                    isOpen,
                                                  )
                                                }
                                                addIn={() =>
                                                  setAddInElement(element)
                                                }
                                                addAfter={() =>
                                                  setAddAfterElement(element)
                                                }
                                                minDate={ctx.minDate}
                                                maxDate={ctx.maxDate}
                                              />
                                            ) : isTask ? (
                                              <Task
                                                task={element}
                                                meta={elMeta}
                                                parentList={parentList}
                                                resource={{
                                                  ...resource,
                                                  uuid: workLoad.uuid,
                                                }}
                                                minDate={
                                                  breadcrumbs[
                                                    breadcrumbs.length - 1
                                                  ]?.startDate || ctx.minDate
                                                }
                                                maxDate={ctx.maxDate}
                                              />
                                            ) : isMilestone ? (
                                              <Milestone
                                                milestone={element}
                                                meta={elMeta}
                                                minDate={
                                                  breadcrumbs[
                                                    breadcrumbs.length - 1
                                                  ]?.startDate || ctx.minDate
                                                }
                                                maxDate={ctx.maxDate}
                                              />
                                            ) : isEvent ? (
                                              <Event
                                                event={element}
                                                meta={elMeta}
                                                parentList={parentList}
                                                minDate={
                                                  breadcrumbs[
                                                    breadcrumbs.length - 1
                                                  ]?.startDate || ctx.minDate
                                                }
                                                maxDate={ctx.maxDate}
                                              />
                                            ) : (
                                              <></>
                                            )}
                                          </Draggable>
                                          <Preview
                                            canDrop={canDrop}
                                            isList={isList}
                                          />
                                        </QuickCreationMenuWrapper>
                                      )}
                                    </DropBox>
                                  </RenderIfVisible>
                                </div>

                                {isListEnd &&
                                  new Array(
                                    Math.max(
                                      (!!ganttByProject[index + 1]
                                        ? element.depth -
                                          ganttByProject[index + 1].depth
                                        : element.depth) - (isList ? 1 : 0),
                                      0,
                                    ),
                                  )
                                    .fill(0)
                                    .map((_, idx) => (
                                      <DropBox
                                        key={`drop-${idx}`}
                                        type="dropAfterList"
                                        gantt={ganttByProject}
                                        nextParent={
                                          breadcrumbs[idx]?.parentUuid ===
                                          project.uuid
                                            ? project.rootList
                                            : {
                                                ...list,
                                                uuid: breadcrumbs[idx]
                                                  ?.parentUuid,
                                              }
                                        }
                                        projectElement={element}
                                        index={idx}
                                        style={
                                          typeof style.indentSize === "number"
                                            ? {
                                                marginLeft: `${
                                                  style.indentSize * idx +
                                                  style.indentSize
                                                }px`,
                                              }
                                            : {
                                                marginLeft: `${3 * idx + 3}rem`,
                                              }
                                        }
                                      />
                                    ))}

                                {addInElement &&
                                  addInElement.uuid === element.uuid && (
                                    <div
                                      ref={inRef}
                                      className={styles.elementCreationForm}
                                      style={{
                                        marginLeft: `calc(${element.depth + 2} * ${indentSize})`,
                                        height:
                                          style.itemHeight ||
                                          DEFAULT_ESTIMATED_HEIGHT,
                                      }}
                                    >
                                      <ElementCreationForm
                                        project={project}
                                        parentList={{ ...list, ...element }}
                                        range={{ min: null, max: null }}
                                        loader={(type, name) => (
                                          <QuickElementLoader
                                            type={type}
                                            name={name}
                                            parentList={{ ...list, ...element }}
                                          />
                                        )}
                                        done={() => setAddInElement(null)}
                                      />
                                    </div>
                                  )}

                                {addAfterAtElement &&
                                  canAddAfter(
                                    ganttByProject,
                                    addAfterAtElement,
                                    breadcrumbs,
                                    index,
                                  ) && (
                                    <div
                                      ref={afterRef}
                                      className={styles.elementCreationForm}
                                      style={{
                                        marginLeft: `calc(${element.depth + 1} * ${indentSize})`,
                                        minHeight:
                                          style.itemHeight ||
                                          DEFAULT_ESTIMATED_HEIGHT,
                                        height: 0,
                                      }}
                                    >
                                      <ElementCreationForm
                                        project={project}
                                        parentList={
                                          element.depth === 0
                                            ? project.rootList
                                            : {
                                                ...list,
                                                uuid: element.parentUuid,
                                              }
                                        }
                                        range={{
                                          min: ganttByProject[index]
                                            ? {
                                                ...ganttByProject[index],
                                                rank: ganttByProject[index]
                                                  .referenceKeyRank,
                                              }
                                            : null,
                                          max: ganttByProject[index + 1]
                                            ? {
                                                ...ganttByProject[index + 1],
                                                rank: ganttByProject[index + 1]
                                                  .referenceKeyRank,
                                              }
                                            : null,
                                        }}
                                        loader={(type, name) => (
                                          <QuickElementLoader
                                            type={type}
                                            name={name}
                                            parentList={{ ...list, ...element }}
                                          />
                                        )}
                                        defaultType={ElementTypes.LIST}
                                        done={() => setAddAfterElement(null)}
                                      />
                                    </div>
                                  )}
                              </div>
                            );
                          },
                        )}
                      </>
                    );
                  }}
                </ProjectCtx>
              ))}
            </>
          );
        }}
      </ResourceCtx>
    );
  };

  return (
    <Context.Provider
      value={{
        hoveredElement,
        configuration,
        style: {
          ...style,
          itemHeight: style.itemHeight || DEFAULT_ESTIMATED_HEIGHT,
        },
      }}
    >
      <DragAndDrop>
        <div className={styles.wbsByResource}>
          {filteredWorkLoads.map((workLoad) => {
            const isResourceOpen = isOpen(workLoad.uuid);

            return (
              <div key={workLoad.uuid} className={styles.resourceSection}>
                <div
                  className={styles.resourceHeader}
                  style={{
                    height: style.itemHeight || DEFAULT_ESTIMATED_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid var(--color-grey)",
                    fontWeight: "bold",
                  }}
                >
                  <Resource
                    workLoad={workLoad}
                    isOpen={isResourceOpen}
                    toggle={(isOpen) => toggleResource(workLoad.uuid, isOpen)}
                  />
                </div>
                {isResourceOpen && (
                  <div className={styles.resourceElements}>
                    {renderResourceElements(workLoad)}
                    {isFetchingProjects && <ProjectLoader style={style} />}
                  </div>
                )}
              </div>
            );
          })}
          {isFetchingWorkloads && <ProjectLoader style={style} />}
        </div>
      </DragAndDrop>
    </Context.Provider>
  );
}

export { Wbs };
